import { getRendererFromDescriptor, PageLayout } from '@kizi/comparator-core';
import {
  registerAnalyticsAddToCartEvent,
  registerAnalyticsViewItemEvent,
  registerAnalyticsViewListEvent,
  registerTopThreeClickEvent,
} from '../../../utils/analytics';
import { getTabs } from '../../../utils/overview';
import {
  SORTING_DESCENDING_ORDER_TRIPLE_PLAY,
  SORTING_OPTIONS_TRIPLE_PLAY,
  SORTING_REFERENCE_TRIPLE_PLAY,
} from '../../../utils/sorting';
import { faqItems, howWeCompareItems } from '../content/modalsContent';
import triplePlayProvvalueerOptions from '../content/osTriplePlayProviderOptions';

const personalData = {
  type: 'PersonalData2',
  props: {
    p: ['0 16px 16px !important', '0 24px 16px !important'],
    mb: 3,
    borderRadius: '24px',
    variant: 'greyTooltipPrimaryCard',
    fieldNames: [
      'startWidget.fullAddress',
      'startWidget.postcode',
      'startWidget.housenumber',
      'startWidget.housenumberaddition',
      'startWidget.street',
      'startWidget.city',
      'overview.productTypes',
      'startWidget.currentProvider',
    ],
    viewModeLayout: [
      {
        type: 'Div',
        props: {
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gridGap: 2,
        },
        _children: [
          {
            type: 'Div',
            props: {
              gridGap: 1,
            },
            _children: [
              {
                type: 'PersonalDataFieldViewer',
                props: {
                  name: 'fullAddress',
                },
              },
              {
                type: 'LabelWithFieldname',
                props: {
                  label: 'Huidige provider:',
                  mt: 3,
                  options: 'startWidget.providerOptions',
                  selected: 'startWidget.currentProvider',
                },
              },
            ],
          },
        ],
      },
    ],
    formLayout: [
      {
        type: 'Div',
        props: {
          pt: 1,
          mb: '15px',
          width: '100%',
        },
      },
      {
        type: 'AddressWithContext',
        props: {
          autoForward: true,
          inputProps: {
            postcode: {
              _label: 'Postcode',
              placeholder: '1234AB',
            },
            houseNumber: {
              _label: 'Huisnr.',
              placeholder: '123',
              type: 'tel',
            },
            addition: {
              _label: 'Toev.',
              placeholder: 'A',
            },
          },
          fieldNames: {
            addition: 'housenumberaddition',
            houseNumber: 'housenumber',
            postcode: 'postcode',
            city: 'city',
            street: 'street',
            fullAddress: 'fullAddress',
          },
        },
      },
      {
        type: 'FormInput',
        props: {
          name: 'currentProvider',
          component: {
            type: 'Inputs.Select',
            props: {
              _containerProps: { mt: 3 },
              _label: 'Je huidige provider',
              _tooltip:
                'Selecteer je huidige provider om tot de beste deals te komen',
              options: triplePlayProvvalueerOptions,
              optionsFieldName: 'startWidget.providerOptions',
            },
          },
        },
      },
    ],
    viewModeFooter: [
      {
        type: 'SliderControl',
        props: {
          goto: 1,
          variant: 'transparent',
          mt: 3,
          fontSize: [2, 3],
          children: getRendererFromDescriptor({
            type: 'Div',
            props: {
              display: 'flex',
              padding: '4px',
              borderRadius: '80px',
              background:
                'linear-gradient(315deg, #03AD7D 11.93%, #08C98F 81.44%)',
              boxShadow:
                '0px 4px 8px 0px rgba(62, 72, 101, 0.05), 0px 2px 4px 0px rgba(78, 90, 126, 0.10), 0px 4px 8px 0px rgba(0, 0, 0, 0.05)',
            },
            _children: [
              {
                type: 'IconV2',
                props: {
                  name: 'pencil',
                  size: 24,
                },
              },
            ],
          }),
        },
      },
    ],
  },
};

const overviewLayout: PageLayout = {
  name: 'overview',
  components: [
    {
      type: 'OverviewPageNew',
      props: {
        paramsFromFields: [
          'startWidget.postcode',
          'startWidget.housenumber',
          'startWidget.housenumberaddition',
          'startWidget.currentProvider',
          'overview.productTypes',
          'overview.additionalChannels',
        ],
        filtersToIgnore: ['productTypes'],
        filters: [
          {
            type: 'component',
            containerProps: { display: ['block', 'none'] },
            componentProps: {
              type: 'CombinationDiscount',
              props: {
                isFilter: true,
              },
            },
          },
          {
            type: 'component',
            id: 'divider',
            containerProps: { display: ['block', 'none'] },
            componentProps: {
              type: 'Div',
              props: {
                borderTop: '1px solid',
                borderColor: 'divider',
              },
            },
          },
          {
            type: 'component',
            id: 'productTypes',
            componentProps: {
              type: 'PackageCheckbox',
              props: {
                label: 'Pakket',
                name: 'productTypes',
                _fieldName: 'overview.productTypes',
                _containerProps: { mt: 3 },
                divProps: { gridGap: [2, 2, 1] },
                labelProps: { marginLeft: [3, 1] },
              },
            },
          },
          {
            type: 'slider',
            id: 'monthlyPrice',
            key: 'priceInfo.total',
            title: 'Prijs (per maand)',
            minValue: 0,
            maxValue: 150,
            valueProps: {
              fontSize: 3,
              textAlign: 'center',
              width: '250px',
            },
          },
          {
            id: 'suppliers',
            isToggleVisible: true,
            key: 'provider.name',
            title: 'Providers',
            type: 'checkbox',
            fields: [],
            shortCheckboxesGroupAmount: 3,
          },
          {
            id: 'duration',
            key: 'durationInMonths',
            preserveFields: true,
            fields: [
              // { label: '1 maand', value: '1' },
              { label: '12 maanden', value: '12' },
              { label: '24 maanden', value: '24' },
            ],
            title: 'Looptijd',
            type: 'checkbox',
          },
          {
            type: 'slider',
            id: 'downloadSpeed',
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'internet',
            },
            leftValue: 50,
            max: 8000,
            customValues: [50, 100, 400, 1000, 8000],
            sectionHeader: 'Internet',
            sectionHeaderIcon: 'newInternet',
            headingProps: {
              fontSize: '24px!important',
              mt: 3,
              borderTopColor: 'second.1',
              borderTopStyle: 'solid',
              borderTopWidth: '1px',
            },
            valueProps: {
              fontSize: 3,
              textAlign: 'center',
              width: '250px',
            },
            unit: 'Mbit/s',
            unitPosition: 'right',
            key: 'downloadSpeedInMb',
            title: 'Downloadsnelheid',
            tooltip:
              'Geef aan van welke downloadsnelheid je minimaal gebruik wil maken.',
            // children: getRendererFromDescriptor({
            //   type: 'Div',
            //   props: {
            //     display: 'flex',
            //     alignItems: 'center',
            //     justifyContent: 'center',
            //     gridGap: '8px',
            //   },
            //   _children: [
            //     {
            //       type: 'IconV2',
            //       props: {
            //         name: 'speed-meter',
            //         size: 24,
            //         mt: 4,
            //       },
            //     },
            //     {
            //       type: 'Button',
            //       props: {
            //         mt: 4,
            //         variant: 'transparent',
            //         children: 'Wat heb ik nodig?',
            //       },
            //     },
            //   ],
            // }),
          },
          {
            id: 'signal',
            key: 'network',
            preserveFields: true,
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'internet',
            },
            fields: [
              { label: 'DSL', value: 'DSL' },
              { label: 'Kabel', value: 'Kabel' },
              { label: 'Glasvezel', value: 'Glasvezel' },
            ],
            title: 'Type verbinding',
            type: 'checkbox',
            tooltip: 'Geef aan van welke type verbinding je gebruik wil maken',
          },
          {
            id: 'extraOptions',
            key: 'reviewAndPause',
            preserveFields: true,
            sectionHeader: 'TV',
            sectionHeaderIcon: 'tv',
            headingProps: {
              fontSize: '24px!important',
              mt: 3,
              borderTopColor: 'second.1',
              borderTopStyle: 'solid',
              borderTopWidth: '1px',
            },
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'television',
            },
            fields: [
              // {
              //   label: 'Opnemen',
              //   value: 'recordPrograms',
              //   key: 'servicesWrapper.televisionInfo.recordPrograms.available',
              // },
              {
                label: 'Live pauzeren',
                value: true,
              },
              // {
              //   label: 'Begin gemist',
              //   value: 'missedPrograms',
              //   key: 'servicesWrapper.televisionInfo.missedPrograms.available',
              // },
              // {
              //   label: 'Video on demand',
              //   value: 'videoOnDemand',
              //   key: 'servicesWrapper.televisionInfo.videoOnDemand.available',
              // },
            ],
            isToggleVisible: true,
            shortCheckboxesGroupAmount: 4,
            title: 'Extra opties',
            type: 'checkbox',
          },
          {
            type: 'component',
            componentProps: {
              type: 'AdditionalChannels',
            },
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'television',
            },
          },
          {
            id: 'numberPorting',
            key: 'numberPorting',
            preserveFields: true,
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'call',
            },
            // tooltip: '',
            labelProps: {
              marginTop: 0,
            },
            containerProps: {
              borderBottom: 'none!important',
              paddingBottom: '0!important',
            },
            isToggleVisible: true,
            type: 'checkbox',
            sectionHeader: 'Bellen',
            sectionHeaderIcon: 'telephone',
            headingProps: {
              fontSize: '24px!important',
              mt: 3,
              mb: 0,
              borderTopColor: 'second.1',
              borderTopStyle: 'solid',
              borderTopWidth: '1px',
            },
            fields: [
              {
                label: 'Nummerbehoud',
                value: true,
              },
            ],
          },
          {
            id: 'unlimitedCallingInNetherlands',
            key: 'unlimitedCallingInNetherlands',
            preserveFields: true,
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'call',
            },
            containerProps: {
              borderBottom: 'none!important',
              paddingBottom: '0!important',
            },
            isToggleVisible: true,
            type: 'checkbox',
            fields: [
              {
                label: 'Onbeperkt bellen NL',
                value: 'true',
              },
            ],
          },
          {
            id: 'unlimitedCallingInEurope',
            key: 'unlimitedCallingInEurope',
            preserveFields: true,
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'call',
            },
            isToggleVisible: true,
            type: 'checkbox',
            fields: [
              {
                label: 'Onbeperkt bellen EU',
                value: 'true',
              },
            ],
          },
        ],
        bg: 'second.2',
        navPosition: 'left',
        productsRequest: {},
        selectedSorting: 0,
        selectedFilters: {
          showPriceMode: ['month'],
          monthlyPrice: [0, 100],
          suppliers: [],
          downloadSpeed: [50, 8000],
          signal: ['DSL', 'Kabel', 'Glasvezel', '5G'],
          extraOptions: [],
          duration: ['12'],
          numberPorting: [],
          unlimitedCallingInNetherlands: [],
          unlimitedCallingInEurope: [],
        },
        sortingOptions: [
          {
            label: 'Aanbevolen',
            value: SORTING_OPTIONS_TRIPLE_PLAY.Recommended,
            icon: 'money',
            tooltip:
              'Dit zijn de meest afgesloten producten in onze vergelijker.',
            hideBg: true,
          },
          {
            label: 'Laagste jaarprijs',
            value: SORTING_OPTIONS_TRIPLE_PLAY.Year_Price,
            icon: 'money2',
          },
          {
            label: 'Laagste maandprijs',
            value: SORTING_OPTIONS_TRIPLE_PLAY.Month_Price,
            icon: 'money',
          },
          {
            label: 'Hoogste snelheid',
            value: SORTING_OPTIONS_TRIPLE_PLAY.Speed,
            icon: 'money',
          },
        ],
        sortingReference: Object.values(SORTING_REFERENCE_TRIPLE_PLAY),
        sortingDescendingOptions: Object.values(
          SORTING_DESCENDING_ORDER_TRIPLE_PLAY,
        ),
        defaultQueryParams: { type: 'os-triple-play' },
        header: {
          type: 'PageHeader',
          props: {
            steps: [{ title: 'Vergelijken', disabled: true }],
            faq: {
              buttonLabel: 'Veelgestelde vragen',
              buttonIcon: 'comment',
              title: 'Veelgestelde vragen',
              items: faqItems,
              hideLabelOnMobile: true,
            },
            activeStep: 0,
          },
        },
        withTopThree: true,
        promoSection: [
          {
            type: 'TopThreeSectionWrapper',
            props: {
              mb: 6,
              label: 'Beste deals',
            },
            _children: [
              {
                type: 'TopThreeProductsWrapper',
                props: {
                  filters: 'overview.filters',
                  customTopThreeCard: 'ProductCardTriplePlayTopThree',
                  defaultFilters: 'overview.defaultFilters',
                  multiCompare: true,
                  nextIndex: 3,
                  swiperThresholdValues: [0, 85, 153],
                  hasPromotionBar: true,
                  wrapperStyles: {
                    'div': {
                      boxSizing: 'content-box',
                    },
                  },
                  registerAnalyticsAddToCartEvent: registerTopThreeClickEvent,
                  promotionBarModals: {
                    items: [
                      {
                        modal: {
                          contentType: 'incentive',
                          title: 'Hoe werkt de cashbackactie?',
                          buttonIcon: 'gift',
                          buttonIconProps: { size: 16 },
                          content: [
                            {
                              type: 'IncentiveContent',
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              },
            ],
          },
        ],
        sideNav: [
          personalData,
          {
            type: 'CombinationDiscount',
          },
          {
            type: 'FiltersWrapper',
            props: {
              title: 'Filters',
              padding: '24px',
              borderRadius: '24px',
              headingProps: {
                m: 0,
                fontSize: '32px!important',
              },
              fields: [
                'overview.filters',
                'overview.defaultFilters',
                'overview.filterGraphQL',
              ],
            },
          },
        ],
        subHeader: [
          {
            type: 'PersonalDataMinimized',
            props: {
              ...personalData.props,
              header: undefined,
              variant: undefined,
              showLocationIcon: false,
              showProvider: true,
              showPencilIcon: true,
              options: triplePlayProvvalueerOptions,
              containerProps: {
                display: ['block', 'block', 'block', 'none'],
                mt: 2,
              },
            },
          },
          {
            type: 'Div',
            props: {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            },
            _children: [
              {
                type: 'Div',
                props: {
                  display: 'flex',
                  flexDirection: 'column',
                  gridGap: 3,
                  my: 32,
                },
                _children: [
                  { type: 'OverviewTopMessage' },
                  { type: 'AvailableNetworks' },
                ],
              },
            ],
          },
        ],
        content: [
          {
            type: 'Div',
            props: {
              mb: 4,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            },
            _children: [
              {
                type: 'Div',
                props: {
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gridGap: 2,
                },
                _children: [
                  {
                    type: 'Div',
                    props: {
                      display: ['none', null, 'initial'],
                    },
                    _children: [
                      {
                        type: 'ModalWithAccordion',
                        props: {
                          buttonLabel: 'Hoe vergelijken wij?',
                          buttonIcon: 'checked-doc',
                          items: howWeCompareItems,
                          title: 'Hoe vergelijken wij?',
                        },
                      },
                    ],
                  },
                ],
              },
              {
                type: 'FiltersSortingWrapper',
                props: {
                  width: ['100%', '100%', 'initial'],
                  title: 'Sorteren op:',
                  filtersNameField: 'overview.filters',
                  filtersTitle: 'Filters',
                  hideResetFiltersOption: false,
                  showIcon: true,
                },
              },
            ],
          },
          {
            type: 'ProductsNewWrapper',
            props: {
              nextIndex: 3,
              filters: 'overview.filters',
              defaultFilters: 'overview.defaultFilters',
              customProductContent: 'ProductCardTriplePlay',
              disableLoadingSkeleton: false,
              multiCompare: true,
              promotionBarModals: {
                items: [
                  {
                    modal: {
                      contentType: 'incentive',
                      title: 'Hoe werkt de cashbackactie?',
                      buttonIcon: 'gift',
                      buttonIconProps: { size: 16 },
                      content: [
                        {
                          type: 'IncentiveContent',
                        },
                      ],
                    },
                  },
                ],
              },
              additionalInfo: {
                path: 'servicesWrapper.networkInformation.internetSpeed.downloadSpeedInMb',
                measure: 'Mbit/s',
                css: {
                  'span:last-child': {
                    pb: '3px',
                    borderBottom: '1px dashed',
                    borderColor: 'lightMain1',
                  },
                },
                windowProps: {
                  type: 'popover',
                  popoverProps: {
                    arrow: true,
                    position: 'top',
                  },
                },
                component: {
                  type: 'SpeedPopup',
                },
              },
              priceProps: {
                variant: 'priceBigWithBorder',
              },
              sustainabilityProps: {
                tabToOpen: 0,
              },
              hideProductUsps: true,
              getTabs,
              registerAnalyticsViewItemEvent,
              registerAnalyticsAddToCartEvent,
            },
          },
          {
            type: 'Div',
            props: {
              height: [20, 35, 50],
              width: '100%',
            },
          },
        ],
        registerAnalyticsViewListEvent,
        affiliatePageDescriptor: {
          type: 'AffiliateUrlModalWrapper',
        },
        affiliateUrlClientId: 'overstappen',
      },
    },
  ],
};

export default overviewLayout;
